import { useCallback, useMemo } from 'react'
import { Encoding, IReceiptlineConfig } from 'receiptline'
import receiptline from 'receiptline'
import { PrinterContract, PrinterMode } from 'models'
import { isUndefined } from 'lodash-es'
import { convertSvgToPngAsync } from 'utilities/svgConverter'

export interface IReceiptLineProps {
    onScreenPrinter?: PrinterContract
    defaultPrinter?: PrinterContract
}

/** list of Receiptline command modes which needs to be rendered with image */
const renderedWithImageModes = ['svg', 'stargraphic']

const toCommandMode = (printerMode: PrinterMode | undefined) => {
    switch (printerMode) {
        case PrinterMode.EscPos:
            return 'escpos'
        case PrinterMode.Image:
            return 'svg'
        case PrinterMode.DotMatrix:
            return 'impactb'
        case PrinterMode.Generic:
            return 'generic'
        case PrinterMode.StarGraphic:
            return 'stargraphic'
        case PrinterMode.StarImpact1:
            return 'starimpact'
        case PrinterMode.StarImpact2:
            return 'starimpact2'
        case PrinterMode.StarImpact3:
            return 'starimpact3'
        default:
            return 'svg'
    }
}

const isGraphicMode = (printerMode: PrinterMode | undefined) => {
    return (
        printerMode !== PrinterMode.EscPos && printerMode !== PrinterMode.Image
    )
}

const useReceiptLine = (props: IReceiptLineProps) => {
    const { onScreenPrinter, defaultPrinter } = props

    const onScreenConfig = useMemo((): IReceiptlineConfig => {
        return {
            command: 'svg',
            cpl: onScreenPrinter?.charactersPerLine || 42,
            encoding: (onScreenPrinter?.encoding as Encoding) || 'cp437',
        }
    }, [onScreenPrinter])
    const printerConfig = useMemo((): IReceiptlineConfig => {
        return {
            command: toCommandMode(defaultPrinter?.printerMode),
            threshold: isGraphicMode(defaultPrinter?.printerMode)
                ? defaultPrinter?.threshold || 225
                : undefined,
            // Turn off gradient for Dot Matrix printer to increase sharpness
            gradient: defaultPrinter?.printerMode !== PrinterMode.DotMatrix,
            cpl: defaultPrinter?.charactersPerLine || 48,
            encoding: (defaultPrinter?.encoding as Encoding) || 'cp437',
            upsideDown: false,
            spacing: true,
        }
    }, [defaultPrinter])

    const svgToBase64 = (svg: any) => {
        const data = btoa(unescape(encodeURIComponent(svg)))
        return data
    }

    const getOverridePrinter = (
        overridePrinter: PrinterContract,
    ): IReceiptlineConfig => {
        return {
            command: toCommandMode(overridePrinter?.printerMode),
            threshold: isGraphicMode(overridePrinter?.printerMode)
                ? overridePrinter?.threshold || 225
                : undefined,
            cpl: overridePrinter?.charactersPerLine || 48,
            encoding: (overridePrinter?.encoding as Encoding) || 'cp437',
            upsideDown: false,
            spacing: true,
        }
    }

    /**
     * transform receiptline markup to text/binary command
     * mode is specified on `printerConfig.command`
     * If mode
     *  - `svg` -> the output is "SVG Data URL"
     *  - `stargraphic` -> the output is stargraphic command bytes which is generated from Markup -> SVG -> PNG -> StarGraphic bytes
     *  - other mode will be handled with `receiptline.transform` function directly
     */
    const transformReceiptCommand = useCallback(
        async (markup: string, overridePrinter?: PrinterContract) => {
            let _printerConfig = printerConfig

            if (!isUndefined(overridePrinter))
                _printerConfig = getOverridePrinter(overridePrinter)

            const needToRenderWithImage = renderedWithImageModes.includes(
                _printerConfig.command || '',
            )

            let command = receiptline.transform(markup, {
                ..._printerConfig,
                // if that mode needed image rendering, we render to SVG image first
                command: needToRenderWithImage ? 'svg' : _printerConfig.command,
            })
            if (needToRenderWithImage) {
                const base64 = svgToBase64(command)
                command = `data:image/svg+xml;base64,${base64}`

                // in case of `stargraphic` mode we need to convert back to markup `{image: [PNG in base64]}`
                // then, use `receiptline.transform` function to convert this to Star Printer command to print raster image
                if (_printerConfig.command === 'stargraphic') {
                    const pngDataUrl = await convertSvgToPngAsync(command)
                    const pngBase64 = pngDataUrl.split(',')[1]
                    const receiptLineMd = `{image: ${pngBase64}}`

                    // Convert receiptline PNG-markup to Star Command with `stargraphic` mode
                    command = receiptline.transform(receiptLineMd, {
                        ..._printerConfig,
                        command: 'stargraphic',
                        // turn off the gredient
                        // because the `threshold` params, specified in `_printerConfig` won't take any effect while gredient is TRUE (it's TRUE by default )
                        // with image thresholding, the output image data will only contain black or white
                        // the `threshold` is the gray-level intensity (from 0[extreme black] to 255[extreme white]) value that define the threshold
                        // For example, if `threshold` = 140, this means the pixel that has intensity level below 140, will turn to 0 (black), otherwise will turn to 255 (white)
                        gradient: false,
                    })
                }
            }

            return command
        },
        [printerConfig],
    )

    const transformReceiptSvgBase64 = useCallback(
        (markup: string) => {
            const svg = receiptline.transform(markup, onScreenConfig)
            const base64 = svgToBase64(svg)
            return `data:image/svg+xml;base64,${base64}`
        },
        [onScreenConfig],
    )
    return {
        transformReceiptCommand,
        transformReceiptSvgBase64,
    }
}

export default useReceiptLine
